import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Play, Clock } from 'lucide-react';
import { useContent } from '../hooks/useContent';
import { SectionHeading } from './SectionHeading';

const Videos = () => {
  const { data: videos, loading } = useContent('videos', []);
  const items = videos || [];
  if (!loading && items.length === 0) return null;

  return (
    <section id="videos" className="py-24 bg-gradient-to-br from-[#0A1628] via-[#0D2444] to-[#0A1628] relative overflow-hidden" data-testid="videos-section">
      {/* decorative */}
      <div className="absolute -top-32 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-32 right-1/4 w-96 h-96 bg-rose-500/10 rounded-full blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-4">
        <div className="text-center mb-12 md:mb-16">
          <Badge className="mb-4 bg-amber-500/20 text-amber-300 border-amber-500/30 px-3 py-1 text-xs font-semibold uppercase tracking-widest">
            Videos
          </Badge>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-white mb-4 leading-tight">
            Watch &{' '}
            <span className="italic bg-gradient-to-r from-amber-300 via-orange-300 to-rose-300 bg-clip-text text-transparent">
              Learn
            </span>
          </h2>
          <div className="h-1.5 w-24 bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 rounded-full mx-auto mb-4"></div>
          <p className="text-base md:text-lg text-white/80 max-w-3xl mx-auto">
            Documentaries, testimonials, and event highlights from EWRF.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {items.map((video) => {
            const Wrapper = video.video_url ? 'a' : 'div';
            const wrapperProps = video.video_url
              ? { href: video.video_url, target: '_blank', rel: 'noreferrer' }
              : {};
            return (
              <Card key={video.id} className="group bg-white/5 backdrop-blur-sm border border-white/10 hover:border-amber-400/40 hover:bg-white/10 transition-all overflow-hidden">
                <CardContent className="p-0">
                  <Wrapper {...wrapperProps}>
                    <div className="relative overflow-hidden aspect-video bg-gray-900">
                      {video.thumbnail && (
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/90 via-[#0A1628]/30 to-transparent flex items-center justify-center">
                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                          <Play className="w-8 h-8 text-white fill-white ml-1" />
                        </div>
                      </div>
                      {video.duration && (
                        <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-md text-white text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5">
                          <Clock className="w-3 h-3" />
                          {video.duration}
                        </div>
                      )}
                    </div>
                  </Wrapper>
                  <div className="p-6">
                    {video.type && (
                      <Badge className="mb-3 bg-amber-500/20 text-amber-300 border-amber-500/30 text-[10px] font-bold uppercase tracking-widest">
                        {video.type}
                      </Badge>
                    )}
                    <h4 className="text-lg md:text-xl font-bold text-white mb-2 group-hover:text-amber-300 transition-colors">{video.title}</h4>
                    <p className="text-sm text-white/70 line-clamp-2">{video.description}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Videos;
